import React, { useEffect, useState } from 'react';
import ModalDialog from '@common/ui/modal/ModalDialog';
import createDecorator from 'final-form-focus';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import formutils from '@utils/formutils';
import api from '@common/utils/api';
import DwFormField from '@common/ui/formfield/DwFormField';
import Button from '@common/ui/button/Button';
import Accordion from '@common/ui/accordion/Accordion';
import AccordionItem from '@common/ui/accordion/AccordionItem';
import ErrorBox from '@components/common/ErrorBox';
import analytics from '@utils/analytics';
const focusOnError = createDecorator(); // must be outside the component
/**
 * A modal which allows the user to check the remaining balance of a gift card
 */
const GiftCardBalanceModal = ({ isOpen = false, onRequestClose, pid }) => {
    // States
    const [modalOpen, setModalOpen] = useState(false); // *true* opens the modal
    const [errors, setErrors] = useState([]); // Form errors
    const [formdef, setFormdef] = useState(); // Form definition
    const [initialValues, setInitialValues] = useState(); // Form initial values
    const [step, setStep] = useState('form'); // Define the step to show on the page
    const [cardNumber, setCardNumber] = useState(''); // Card number
    const [pin, setPin] = useState(''); // PIN code - currently not used
    const [expirationDate, setExpirationDate] = useState(''); // Card expiration
    const [balance, setBalance] = useState(''); // Remaining balance
    // Hooks
    const { t } = useTranslation();
    // Boot
    useEffect(() => {
        // In order to allow editorial uses of this component, we need an event listener
        // on a class that can open and close the modal. This is indipendent from `isOpen` prop
        document.querySelectorAll('.gc-balance-show').forEach(a => {
            loadForm();
            a.addEventListener('click', () => setModalOpen(true), false);
        });
    }, []);
    useEffect(() => {
        if (isOpen) {
            loadForm();
        }
        // This is necessary since we have to open and close the modal with a state variable
        setModalOpen(isOpen);
    }, [isOpen]);
    /**
     * [API] Load the form definition
     */
    const loadForm = () => {
        let val = {};
        api.getForm('checkGCBalance', val, true).then(data => {
            setInitialValues(val);
            setFormdef(data);
        });
    };
    /**
     * Validate the form
     */
    const validate = async (values) => {
        return await formutils.validateForm(values, formdef, 'checkGCBalance');
    };
    /**
     * Handle submit errors
     */
    const clearInputError = (formErrors) => {
        var _a, _b;
        const cardNumberInput = (_a = document.getElementsByName('cardNumber')) === null || _a === void 0 ? void 0 : _a[0];
        const pinInput = (_b = document.getElementsByName('pin')) === null || _b === void 0 ? void 0 : _b[0];
        const clearCardNumber = !(formErrors === null || formErrors === void 0 ? void 0 : formErrors.cardNumber) || (formErrors === null || formErrors === void 0 ? void 0 : formErrors.all);
        const clearPin = !(formErrors === null || formErrors === void 0 ? void 0 : formErrors.pin) || (formErrors === null || formErrors === void 0 ? void 0 : formErrors.all);
        clearCardNumber && (cardNumberInput === null || cardNumberInput === void 0 ? void 0 : cardNumberInput.setAttribute('aria-invalid', 'false'));
        clearPin && (pinInput === null || pinInput === void 0 ? void 0 : pinInput.setAttribute('aria-invalid', 'false'));
        if (clearCardNumber && clearPin) {
            setErrors([]);
        }
    };
    /**
     * [API] send a request to the server and check the balance of the gift card
     */
    const checkBalance = async (values) => {
        clearInputError({ all: true });
        let errors = await validate(values);
        if (errors && Object.keys(errors).length > 0) {
            return errors;
        }
        return api
            .checkGiftCardBalance(values, formdef)
            .then(data => {
            var _a, _b;
            if (data.serverErrors) {
                setStep('serverError');
                return;
            }
            if (!data.success && data.fields && data.fields.length > 0) {
                let dataErrors = data.fields.map(err => Object.values(err)[0]);
                // style the inputs as invalid
                const cardNumberInput = (_a = document.getElementsByName('cardNumber')) === null || _a === void 0 ? void 0 : _a[0];
                const pinInput = (_b = document.getElementsByName('pin')) === null || _b === void 0 ? void 0 : _b[0];
                cardNumberInput === null || cardNumberInput === void 0 ? void 0 : cardNumberInput.setAttribute('aria-invalid', 'true');
                pinInput === null || pinInput === void 0 ? void 0 : pinInput.setAttribute('aria-invalid', 'true');
                setErrors(dataErrors);
                return;
            }
            if (!formutils.checkValidationAfterSubmit(data, errors)) {
                return errors;
            }
            if (data.success) {
                setExpirationDate(data.expDate);
                setBalance(data.balance);
                setStep('balance');
            }
        })
            .catch(e => {
            console.log("Error in GiftCardBalanceModal.checkBalance:", e);
            setStep('serverError');
        });
    };
    if (!formdef) {
        return React.createElement(React.Fragment, null);
    }
    /**
     * Step 1: Render the form that asks for the numer and PIN
     */
    const renderForm = () => (React.createElement(React.Fragment, null,
        React.createElement("p", { className: 'mb-32' }, t('gc.check.balancedescription')),
        React.createElement(Form, { onSubmit: checkBalance, initialValues: initialValues, validate: validate, decorators: [focusOnError], render: ({ handleSubmit, form, submitting, pristine, errors: formErrors, values }) => {
                setCardNumber(values.cardNumber);
                setPin(values.pin);
                return (React.createElement("form", { onSubmit: handleSubmit },
                    React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "row" },
                            React.createElement("div", { className: "col-8" },
                                " ",
                                React.createElement(DwFormField, { onChange: e => setCardNumber(e.target.value), onFocus: () => clearInputError(formErrors), def: formdef, name: 'cardNumber', type: 'text', autocomplete: 'off' })),
                            React.createElement("div", { className: "col-4" },
                                React.createElement(DwFormField, { onChange: e => setPin(e.target.value), onFocus: () => clearInputError(formErrors), def: formdef, name: 'pin', type: 'text', autocomplete: 'off' }))),
                        React.createElement("div", { className: "pw-error" },
                            React.createElement(ErrorBox, { errors: !(formErrors === null || formErrors === void 0 ? void 0 : formErrors.cardNumber) && !(formErrors === null || formErrors === void 0 ? void 0 : formErrors.pin) ? errors : [], className: 'gc-error-box' })),
                        React.createElement("div", { className: "col-12" },
                            React.createElement(Accordion, null,
                                React.createElement(AccordionItem, { accordionId: 'gc-accordion-link', title: t('gc.where.info'), miniAccordion: true },
                                    React.createElement("div", { className: "card-placeholder-container" },
                                        React.createElement("p", { className: 'card-placeholder-desc' }, t('gc.where.infodescription')),
                                        React.createElement("div", { className: "card-placeholder" }))))),
                        React.createElement("div", { className: "row button-row" },
                            React.createElement("div", { className: "col-12" },
                                React.createElement(Button, { onClick: () => {
                                        analytics.checkGiftcardBalance(pid || 'giftcard');
                                    }, type: "submit", disabled: submitting, cls: ['button--primary'] }, t('gc.check.button')))))));
            } })));
    /**
     * Step 2: if card data are correct, show the retrieved info
     */
    const renderBalance = () => (React.createElement(React.Fragment, null,
        React.createElement("p", { className: 'mb-32' }, t('gc.balance.checkinfo')),
        React.createElement("div", { className: 'balance-container' },
            React.createElement("div", { className: "row mb-16" },
                React.createElement("div", { className: "col-12" },
                    React.createElement("p", null,
                        t('gc.cardnumber'),
                        ": ",
                        cardNumber,
                        "\u00A0\u00A0\u00A0\u00A0\u00A0",
                        t('gc.pin'),
                        ": ****"))),
            React.createElement("div", { className: "row mb-16" },
                React.createElement("div", { className: "col-12" },
                    t('gc.balance.term'),
                    ": ",
                    balance)),
            React.createElement("div", { className: "row" },
                React.createElement("div", { className: "col-12" },
                    React.createElement(Button, { type: 'button', className: 'check-another', cls: ['button--secondary'], onClick: () => {
                            setInitialValues({ cardNumber: '', pin: '' }); // We need to reset the previous data in order to prevent some loop errors
                            setStep('form');
                        } }, t('gc.checkanother')))))));
    /**
     * Step 3: if server error, show the error message
     */
    const renderServerError = () => (React.createElement("div", { className: "gc-error-modal" },
        React.createElement("h2", null, t('gc.errorunavailable', 'SERVICE NOT AVAILABLE')),
        React.createElement("p", null, t('gc.errorunavailablemsg', 'We’re sorry the Check Balance service is currently unavailable, please try again later.'))));
    return (React.createElement(ModalDialog, { className: 'modal--small modal--bottom-small modal--fixed delete-account', overlayClassName: "overlay-visible", isOpen: modalOpen, onRequestClose: () => {
            setModalOpen(false);
            setErrors([]);
            onRequestClose();
            setStep('form');
        }, contentLabel: step == 'form' ? t('gc.check.balance') : step == 'balance' ? t('gc.your.balance', 'Your Gift Card') : '' }, step == 'form' ?
        renderForm()
        : step == 'balance' ?
            renderBalance()
            : step == 'serverError' ?
                renderServerError()
                : React.createElement(React.Fragment, null)));
};
export default GiftCardBalanceModal;
